/**
 * Who gets a service worker.
 *
 * Default OFF. A registered worker is the one part of this feature that
 * reverting a deploy does not undo — it lives in the browser and keeps
 * controlling pages — so it reaches an internal pilot tenant before anyone
 * else, and only by being named.
 *
 * ⚠️ The half of this that is easy to get wrong: turning the flag off does
 * NOT remove workers already installed. A browser that registered one while
 * the tenant was in the pilot keeps it forever otherwise. So the policy has
 * three outcomes, not two — and 'remove' is the one that makes the flag a
 * real control rather than a note about future visitors.
 */
export type RegistrationAction = 'register' | 'remove' | 'leave-alone';

export interface RegistrationInput {
  /** Tenants in the pilot, from VITE_PWA_PILOT_TENANTS. Empty = nobody. */
  pilotTenants: string[];
  /** The workspace in use, or null before sign-in. */
  tenantSlug: string | null;
  /**
   * Whether this browser is carrying anything of ours — a registration OR
   * leftover `kp-sw-*` caches.
   *
   * ⚠️ Registrations alone is not enough, and dev proved it. Unregistering
   * does not stop the old worker controlling the page it is already on, and
   * that worker can re-create its cache while serving one more fetch. The
   * registration is then gone, this returns `leave-alone` forever, and the
   * cache is orphaned with nothing left that would ever delete it.
   */
  carriesWorkerOrCaches: boolean;
}

export const parsePilotTenants = (raw: string | undefined | null): string[] =>
  (raw ?? '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

export const decideRegistration = (i: RegistrationInput): RegistrationAction => {
  // "*" means every workspace. ⚠️ DELIBERATELY not the same as an empty list:
  // empty is somebody who forgot and still means NOBODY, while "*" is
  // something a person typed. Identical semantics to the server's
  // WEB_PUSH_PILOT_TENANTS (notification-service internal/config/config.go),
  // because the two gates have to agree: the server telling a tenant desktop
  // notifications are available while this gate refuses them a worker offers
  // a feature that cannot possibly work.
  //
  // An unknown tenant is still unknown under "*" — see the null check below.
  const everyTenant = i.pilotTenants.includes('*');
  const inPilot =
    i.tenantSlug !== null
    && (everyTenant || i.pilotTenants.includes(i.tenantSlug.trim().toLowerCase()));

  if (inPilot) return 'register';

  // ⚠️ Removal requires POSITIVE knowledge that this tenant is out. Signed
  // out, the tenant is unknown — and sessions here expire often, so treating
  // "unknown" as "out" would tear the worker off and reinstall it on every
  // sign-out, discarding the cache and resetting the update state each time.
  if (i.tenantSlug === null) return 'leave-alone';

  // A known tenant that is not in the pilot. If this browser still carries a
  // worker from when it was, take it away — otherwise the flag only ever
  // controls people who have never been here.
  //
  // This does NOT cover a browser that never signs in again. Nothing
  // client-side can: that is what the server-side kill switch
  // (KP_SW_KILL=1, src/sw/tombstone.ts) exists for.
  return i.carriesWorkerOrCaches ? 'remove' : 'leave-alone';
};
